import { create } from 'zustand';

import { ApiError, api, setCsrfToken, setUnauthorizedHandler } from '../lib/api.js';

export type AuthStatus = 'loading' | 'unconfigured' | 'unauthenticated' | 'authenticated';

interface SessionResponse {
  authenticated: boolean;
  csrfToken: string | null;
  expiresAt?: string;
}

interface LoginResponse {
  ok: boolean;
  csrfToken: string;
  expiresAt: string;
}

export interface PrismAccount {
  name: string;
  email: string;
}

interface AuthState {
  status: AuthStatus;
  /** The desktop app signs in with a Prism account (the website's); else a dev password. */
  prism: boolean;
  /** The Prism account this app is (or was last) signed in with. */
  account: PrismAccount | null;
  website: string;
  /** A sign-in waiting for its code to be approved on the website. */
  pending: { code: string; approveUrl: string } | null;
  prismStart: () => Promise<void>;
  prismResume: () => Promise<boolean>;
  prismSignOut: () => Promise<void>;
  cancelPending: () => void;
  error: string | null;
  /** Epoch ms until which further attempts are refused by the server. */
  retryAfterMs: number;
  busy: boolean;

  bootstrap: () => Promise<void>;
  login: (password: string) => Promise<boolean>;
  setup: (password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  markUnauthenticated: () => void;
}

let pollTimer: number | undefined;

export const useAuth = create<AuthState>((set, get) => ({
  prism: false,
  account: null,
  website: '',
  pending: null,
  status: 'loading',
  error: null,
  retryAfterMs: 0,
  busy: false,

  /** Ask the server who we are. Called once on app start. */
  bootstrap: async () => {
    try {
      const status = await api.get<{ configured: boolean; prism?: boolean; account?: PrismAccount | null; website?: string }>('/auth/status');
      set({ prism: status.prism === true, account: status.account ?? null, website: status.website ?? '' });
      if (!status.configured) {
        set({ status: 'unconfigured', error: null });
        return;
      }
    } catch (error) {
      set({
        status: 'unauthenticated',
        error: error instanceof ApiError ? error.message : 'Could not reach the server.',
      });
      return;
    }

    try {
      const session = await api.get<SessionResponse>('/auth/session');
      if (session.authenticated && session.csrfToken) {
        setCsrfToken(session.csrfToken);
        set({ status: 'authenticated', csrfToken: session.csrfToken } as never);
      } else {
        setCsrfToken(null);
        set({ status: 'unauthenticated' });
      }
    } catch {
      set({ status: 'unauthenticated' });
    }

    setUnauthorizedHandler(() => get().markUnauthenticated());
  },

  login: async (password: string) => {
    set({ busy: true, error: null, retryAfterMs: 0 });
    try {
      const result = await api.post<LoginResponse>('/auth/login', { password });
      setCsrfToken(result.csrfToken);
      set({ status: 'authenticated', busy: false, error: null } as never);
      return true;
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      const retryAfterSeconds =
        apiError?.status === 429
          ? Number((apiError.body as { retryAfterSeconds?: number })?.retryAfterSeconds ?? 60)
          : 0;
      set({
        busy: false,
        error: apiError?.message ?? 'Sign-in failed. Try again.',
        retryAfterMs: retryAfterSeconds * 1000,
      });
      return false;
    }
  },

  setup: async (password: string) => {
    set({ busy: true, error: null });
    try {
      await api.put('/auth/password', { password });
      set({ busy: false });
      return await get().login(password);
    } catch (error) {
      set({
        busy: false,
        error: error instanceof ApiError ? error.message : 'Could not set the password.',
      });
      return false;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Even if the call fails, drop local state -- the cookie is gone either way.
    }
    setCsrfToken(null);
    set({ status: 'unauthenticated', error: null } as never);
  },

  /** Starts a Prism sign-in: the website approves the code in the browser; this polls until it is. */
  prismStart: async () => {
    set({ busy: true, error: null });
    try {
      const pending = await api.post<{ code: string; approveUrl: string }>('/auth/prism/start');
      set({ pending, busy: false });
      window.open(pending.approveUrl, '_blank', 'noopener,noreferrer');
      window.clearInterval(pollTimer);
      pollTimer = window.setInterval(async () => {
        const current = get().pending;
        if (!current) return window.clearInterval(pollTimer);
        try {
          const r = await api.post<{ state: string; csrfToken?: string; account?: PrismAccount | null }>('/auth/prism/poll', { code: current.code });
          if (r.state === 'approved' && r.csrfToken) {
            window.clearInterval(pollTimer);
            setCsrfToken(r.csrfToken);
            set({ status: 'authenticated', pending: null, account: r.account ?? null, error: null } as never);
          } else if (r.state === 'expired') {
            window.clearInterval(pollTimer);
            set({ pending: null, error: 'The sign-in code expired. Try again.' });
          }
        } catch {
          // keep waiting
        }
      }, 2500);
    } catch (error) {
      set({ busy: false, error: error instanceof ApiError ? error.message : 'Could not reach the Prism Engine Cloud website.' });
    }
  },

  prismResume: async () => {
    set({ busy: true, error: null });
    try {
      const r = await api.post<{ csrfToken: string; account: PrismAccount | null }>('/auth/prism/resume');
      setCsrfToken(r.csrfToken);
      set({ status: 'authenticated', busy: false, account: r.account } as never);
      return true;
    } catch (error) {
      set({ busy: false, error: error instanceof ApiError ? error.message : 'Could not sign in.' });
      if (error instanceof ApiError && error.status === 401) set({ account: null });
      return false;
    }
  },

  prismSignOut: async () => {
    try {
      await api.post('/auth/prism/signout');
    } catch {
      // the session is gone either way
    }
    setCsrfToken(null);
    set({ status: 'unauthenticated', account: null, error: null } as never);
  },

  cancelPending: () => {
    window.clearInterval(pollTimer);
    set({ pending: null });
  },

  markUnauthenticated: () => {
    setCsrfToken(null);
    set({ status: 'unauthenticated', error: null } as never);
  },
}));